import React, { useEffect, useMemo, useState } from "react";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

// HQ sells nothing, so nothing draws its hardware down and the server has no
// usage to work from. Low-at alone still answers "what do I buy", so the panel
// runs there on the numbers already on screen.
const HAS_USAGE = { designshop: true, vibratovault: true };

/**
 * What to buy, and where to buy it.
 *
 * Renders NOTHING when nothing is low. An empty red box every day teaches
 * people to stop looking at it.
 */
export default function NeedsOrderingPanel({ hardware, location }) {
  const [server, setServer] = useState(null);

  // The parent rebuilds this array from a map on every render, so its identity
  // is not stable — key the refetch on the counts themselves instead.
  const sig = useMemo(
    () => hardware.map((h) => `${h.id}:${h.quantity}:${h.lowAt}`).join(","),
    [hardware]
  );

  useEffect(() => {
    if (!HAS_USAGE[location]) return undefined;
    let alive = true;
    fetch(`${API_URL}/api/hardware/reorder?store=${location}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d && Array.isArray(d.items)) setServer(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [location, sig]);

  const rows = useMemo(() => {
    if (server) return server.items.filter((i) => i.needsOrder);
    // Same shape, worked out from what is already on screen. Days left needs
    // usage, so it stays blank rather than guessing.
    return hardware
      // isLow comes from the last full fetch, and an inline edit swaps in a row
      // that still carries the old flag. Work lowness out from the live fields
      // (the server rule: low_at is set and qty <= low_at).
      .filter((h) => h.lowAt != null && (Number(h.quantity) || 0) <= Number(h.lowAt))
      .map((h) => {
        const qty = Number(h.quantity) || 0;
        const pack = Number(h.packQty) || 1;
        const want = (Number(h.lowAt) || 0) * 2;
        return {
          ...h, quantity: qty, packQty: pack, daysLeft: null,
          suggestPacks: Math.max(1, Math.ceil(Math.max(0, want - qty) / pack)),
          estimatedCost: Math.max(1, Math.ceil(Math.max(0, want - qty) / pack)) * (Number(h.price) || 0),
        };
      });
  }, [server, hardware]);

  if (rows.length === 0) return null;

  const total = rows.reduce((s, i) => s + (Number(i.estimatedCost) || 0), 0);
  const cell = { ...S.td, padding: "9px 14px" };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: "#C2410C" }}>
          Needs ordering
        </span>
        <span style={{ ...S.jobCountBadge, borderColor: "#C2410C", color: "#C2410C" }}>{rows.length}</span>
        <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>
          {server
            ? `Under the low mark, or less than ${server.leadDays} days left at the rate it is being used.`
            : "Under the low mark you set."}
          {total > 0 && ` About $${total.toFixed(2)} to restock.`}
        </span>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: "26%" }}>Item</th>
              <th style={{ ...S.th, width: "12%", textAlign: "center" }}>On hand</th>
              <th style={{ ...S.th, width: "9%", textAlign: "center" }}>Low at</th>
              <th style={{ ...S.th, width: "11%", textAlign: "center" }}>Days left</th>
              <th style={{ ...S.th, width: "14%", textAlign: "center" }}>Buy</th>
              <th style={{ ...S.th, width: "16%" }}>Where</th>
              <th style={{ ...S.th, width: "12%", textAlign: "right" }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => {
              const pack = Number(i.packQty) || 1;
              const qty = Number(i.quantity) || 0;
              return (
                <tr key={i.id} style={{ background: "rgba(194,65,15,0.10)" }}>
                  <td style={{ ...cell, fontWeight: 600 }}>
                    {i.name}
                    {i.sku && (
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "rgba(10,10,15,0.58)" }}>{i.sku}</div>
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }}>
                    {qty}
                    {pack > 1 && (
                      <span style={{ color: "rgba(10,10,15,0.45)", fontSize: 12, marginLeft: 6 }}>{(qty / pack).toFixed(1)} pk</span>
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }}>{i.lowAt == null ? "—" : i.lowAt}</td>
                  <td
                    style={{ ...cell, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", color: i.daysLeft == null ? "rgba(10,10,15,0.35)" : "#C2410C", fontWeight: 700 }}
                    title={i.daysLeft == null ? "Nothing has used this yet, so there is no rate to work from." : undefined}
                  >
                    {i.daysLeft == null ? "—" : i.daysLeft}
                  </td>
                  <td style={{ ...cell, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#0A0A0F" }}>
                    {i.suggestPacks} pack{i.suggestPacks !== 1 ? "s" : ""}
                    {i.estimatedCost > 0 && (
                      <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 12, marginLeft: 6 }}>~${Number(i.estimatedCost).toFixed(2)}</span>
                    )}
                  </td>
                  <td style={{ ...cell, fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{i.supplier || "—"}</td>
                  <td style={{ ...cell, textAlign: "right" }}>
                    {i.buyUrl ? (
                      <a
                        href={i.buyUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...S.addBtn, display: "inline-flex", padding: "5px 12px", fontSize: 12, textDecoration: "none" }}
                      >
                        Buy
                      </a>
                    ) : (
                      <span style={{ color: "rgba(10,10,15,0.35)", fontSize: 13 }}>No link</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
